import { Player } from '../types/fantasy';

interface PlayerInfo {
  playerName: string;
  team: string;
  number: number;
  position: string;
  week1Analysis: string;
  projectedPoints?: number;
  adp?: number;
  week1Opponent?: string;
}

interface PositionData {
  position: string;
  positionName: string;
  players: PlayerInfo[];
}

class PlayerDataService {
  private playerData: Map<string, PlayerInfo> = new Map();
  private loaded: boolean = false;

  async loadPlayerData(): Promise<void> {
    if (this.loaded) return;

    const positions = ['QB', 'RB', 'WR', 'TE', 'DEF', 'IDP'];
    
    try {
      const promises = positions.map(async (pos) => {
        try {
          const response = await fetch(`/data/${pos}.json`);
          if (response.ok) {
            const data: PositionData = await response.json();
            data.players.forEach(player => {
              // Create multiple keys for better matching
              const keys = [
                player.playerName.toLowerCase(),
                player.playerName.toLowerCase().replace(/[^a-z0-9]/g, ''),
                // Also try last name first format
                this.getLastNameFirst(player.playerName).toLowerCase(),
                // Add version without apostrophes
                player.playerName.toLowerCase().replace(/'/g, ''),
              ];
              
              keys.forEach(key => {
                this.playerData.set(key, player);
              });
            });
          }
        } catch (err) {
          // Silently handle individual position load failures
        }
      });

      await Promise.all(promises);
      
      // Also load top 250 players if available
      try {
        const response = await fetch('/data/top_250_fantasy_players.json');
        if (response.ok) {
          const data = await response.json();
          // Process top 250 if it has additional info
          if (Array.isArray(data)) {
            data.forEach((player: any) => {
              if (player.playerName) {
                const key = player.playerName.toLowerCase();
                if (!this.playerData.has(key)) {
                  this.playerData.set(key, player);
                }
              }
            });
          }
        }
      } catch (err) {
        // Silently handle top 250 load failure
      }

      this.loaded = true;
    } catch (error) {
      // Silently handle overall data load failure
    }
  }

  private getLastNameFirst(fullName: string): string {
    const parts = fullName.split(' ');
    if (parts.length > 1) {
      return `${parts[parts.length - 1]}, ${parts.slice(0, -1).join(' ')}`;
    }
    return fullName;
  }

  getPlayerInfo(playerName: string): PlayerInfo | null {
    if (!playerName) return null;
    
    // Try exact match first
    let info = this.playerData.get(playerName.toLowerCase());
    if (info) return info;
    
    // Try without apostrophes and special characters (but keep spaces)
    const cleanName = playerName.toLowerCase().replace(/[^a-z0-9 ]/g, '');
    info = this.playerData.get(cleanName);
    if (info) return info;
    
    // Try without any spaces or special characters
    const noSpaceName = playerName.toLowerCase().replace(/[^a-z0-9]/g, '');
    // Search through all keys for a match without spaces
    const entries = Array.from(this.playerData.entries());
    for (const [key, value] of entries) {
      if (key.replace(/[^a-z0-9]/g, '') === noSpaceName) {
        return value;
      }
    }
    
    // Try last name first format
    info = this.playerData.get(this.getLastNameFirst(playerName).toLowerCase());
    if (info) return info;
    
    // Try partial match (last name only)
    const lastName = playerName.split(' ').pop()?.toLowerCase();
    if (lastName) {
      for (const [key, value] of entries) {
        if (key.includes(lastName)) {
          return value;
        }
      }
    }
    
    return null;
  }

  isLoaded(): boolean {
    return this.loaded;
  }
}

export const playerDataService = new PlayerDataService();
